import React, { useMemo } from 'react';
import classNames from 'classnames';
import MetricsCardWithRank from '@/pages/dashboard/components/MetricsCardWithRank';
import { DATA_TYPE_OBJECT } from '@home/utils/common';
import { platforms } from '@/pages/dashboard/pages/overview/_config/platforms';
import MetricsCardTrendChart from '@/pages/dashboard/components/MetricsCardTrendChart';
import { jumpDetail, openWithParams } from '@/pages/dashboard/utils/utils';
import useStudioExplain from '@home/hooks/useStudioExplain';
import { LIGHT_COLOR_ARR } from '@customComp/Chart/_config/_constant';
import { DEFAULT_PLATFORM_DISABLE } from '@home/pages/studio/config';
import { mapObjectValue } from '@home/pages/studio/utils';
import './index.scss';
import LoadingFailed from '@/pages/dashboard/components/LoadingFailed';
import { extractParamsFromCarryFilter, getFilterConfig } from '../utils';
import { JumpType, navigate } from '@home/utils/utils';

const StudioMetricCardRank = (props: any) => {
  const {
    config,
    filter,
    className,
    showChart = false,
    height = 65,
    disablePlatform,
    style,
    apiDataKey,
    errorStyle,
    filterConfig
  } = props || {};
  const {
    titleEn,
    titleCn,
    titleEnChange,
    titleCnChange,
    imgUrl,
    dodData,
    formatRule,
    titleFormatRule,
    metricKey,
    valueDisplayMode,
    rainbowKey,
    isRightCard,
    isLeftCard,
    explainEn,
    explainCn,
    trendConfig,
    link,
    jumpParams = {},
    sliceLength = 4,
    jumpType = JumpType.Window,
    extraCarryParams={},
    xAxisBoundaryGap = true,
    ...restConfig
  } = config || {};
  const { date_type: dataType, isSingle, start_time: startTime, end_time: endTime, payType, selectorList} = filter || {};
  const carryFilter = getFilterConfig(selectorList, filterConfig);
  const { data, errorMsg } = props?.data?.[ apiDataKey || "metricRank" ] || {};
  const {
    breakdown: osList,
    card_list: cardList,
    trend_list: trendList,
  } = data || {};
  const disablePlatformList = (disablePlatform || DEFAULT_PLATFORM_DISABLE).map(
    (name: string) => ({
      full_name: name,
      value: '',
      share_of_all: '',
    }),
  );

  // 获取七彩石指标注释
  const { tipEn, tipCn } = useStudioExplain(rainbowKey, explainEn, explainCn);
  // 根据配置组装对应DodData
  const selectedDataField = DATA_TYPE_OBJECT[dataType] || 'Day';
  const cardData = cardList?.find(
    (card: { metrics_name: string; }) => card?.metrics_name === metricKey,
  );

  const dodValues = Object.entries(dodData?.[selectedDataField] ?? {})
    ?.map(([key, dodItem]: [string, any]) => {
      if (!isSingle && key !== 'change') {
        return null;
      }
      if (isSingle && key === 'change') {
        return null;
      }
      if (key === 'dod' || key === 'sdlw' || key === 'change') {
        const dataToUse = cardData?.[key];
        return {
          titleEn: dodItem?.titleEn,
          titleCn: dodItem?.titleCn,
          formatRule: dodItem?.formatRule,
          extendFormatRule: dodItem?.extendFormatRule,
          thisTime: dataToUse?.this_time || '',
          lastTime: dataToUse?.last_time || '',
          value: dataToUse?.show_value || '',
          thisValue: dataToUse?.this_value || '',
          lastValue: dataToUse?.last_value || '',
        };
      }
      return null; // 如果 key 不符合条件，返回 null
    })
    .filter(Boolean);

  // 合并 PlayStation 数据并创建 rankData , 按照value降序再截取
  const rankData = (osList && osList.length > 0 ? osList : disablePlatformList)
    .filter(
      (item: { name: string; }) => item?.name?.trim().toLowerCase() !== 'others',
    ) // Filter out 'others'
    .reduce((acc, item, index) => {
      const platformName = item?.full_name.trim().toLowerCase();
      // 定义合并后的平台名称
      const mergedPlatformName = platformName.includes('playstation')
        ? 'PlayStation'
        : item?.full_name;
      // 查找已存在的平台
      const existingPlatform = acc.find(
        (p: { nameEn: string; }) => p?.nameEn === mergedPlatformName,
      );
      if (existingPlatform) {
        // 如果已存在，则累加数值
        existingPlatform.value += parseFloat(item?.value) || '';
        existingPlatform.subValue += parseFloat(item?.share_of_all) || '';
      } else {
        // 如果不存在，则创建新对象
        const platform = platforms.find((platformItem) =>
          mergedPlatformName
            ?.toLowerCase()
            .includes(platformItem?.title.toLowerCase()),
        );

        acc.push({
          nameEn: mergedPlatformName,
          value: parseFloat(item?.value) || '',
          subValue: parseFloat(item?.share_of_all) || '',
          icon: platform?.icon,
          disableIcon: platform?.disableIcon,
          color: platform?.color || LIGHT_COLOR_ARR[index],
          valueDisplayMode,
          isBlackImg: platform?.isBlack,
        });
      }

      return acc;
    }, [])
    ?.sort((a: { value: number; }, b: { value: number; }) => b?.value - a?.value)
    ?.slice(0, sliceLength);

  // 根据 isSingle 动态修改 titleEn 和 titleCn
  const titleEN = isSingle ? titleEn : titleEnChange || titleEn;
  const titleCN = isSingle ? titleCn : titleCnChange || titleCn;

  // 筛选器继承参数
  const filterCarry = useMemo(() => {
    // 确保 extraCarryParams 从 props 或其他地方传入
    const baseParams = {
      start: startTime,
      end: endTime,
      timeType: DATA_TYPE_OBJECT[dataType],
      ...(payType ? { payment_type: payType } : {}),
    };
  
    // 合并额外参数
    return { ...baseParams, ...extractParamsFromCarryFilter(extraCarryParams,carryFilter) };
  }, [filter, extraCarryParams, carryFilter]);

  // 卡片下钻
  const onClick = () => {
    if (link) {
      // 筛选器继承跳转
      if ( jumpParams?.filterValue && isSingle && jumpParams?.isComplete && filter?.jumpTime) {
        jumpParams.filterValue.rangeTime = filter?.jumpTime;
      }

      navigate(link, { jumpType, windowParams: filterCarry });
      
      // 下钻逻辑
      // if (jumpParams?.filterValue) {
      //   jumpParams.filterValue = {
      //     ...mapObjectValue(jumpParams.filterValue, filter,),
      //     ...extractParamsFromCarryFilter(extraCarryParams, carryFilter)
      //   };
      // }
      // jumpDetail(link, jumpParams);
    }
  };

  if (errorMsg) {
    return <div style={{
      height: errorStyle?.height,
      color: '#444',
      background: '#fff',
      borderRadius: 8,
    }}><LoadingFailed /></div>;
  }

  return (
    <MetricsCardWithRank
      titleEn={titleEN}
      titleCn={titleCN}
      tipsEn={tipEn}
      tipsCn={tipCn}
      formatRule={titleFormatRule ? titleFormatRule : formatRule}
      rankFormatRule={formatRule}
      link={link ? onClick : null}
      className={classNames(className, {
        'dbh-studio-home-rank-card': !showChart,
        'no-link': !link ,
      })}
      style={style}
      imgUrl={imgUrl}
      value={cardData?.value}
      dodData={dodValues}
      rankData={rankData}
      metricsChart={
        showChart ? (
          <MetricsCardTrendChart
            height={height}
            titleEn={titleEN}
            titleCn={titleCN}
            isRightCard={isRightCard}
            isLeftCard = {isLeftCard}
            metricData={trendList}
            loading={false}
            error={false}
            filterVal={{ timeType: selectedDataField }}
            componentMetrics={[trendConfig?.[selectedDataField] || {}]}
            xAxisBoundaryGap={xAxisBoundaryGap}
          />
        ) : null
      }
      {...restConfig}
    />
  );
};

export default StudioMetricCardRank;
